import React, { Component } from "react";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import TextField from "../../Components/common/TextField";
import Button from "../../Components/common/Button";
import MenuItem from "../../Components/common/MenuItem";
import Loader from "../../Layout/Loader";
import instance from "../../axios";
import { errorHandler } from "../../Helpers";

const PAYMENT_TYPES = ["INVOICE", "ADVANCE", "PARTIAL"];

const generateIdempotentKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

class AddPaymentForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      type: "INVOICE",
      amount: "",
      description: "",
      invoiceSearch: "",
      invoices: [],
      selectedIds: [],
      invoicesLoader: false,
      submitLoader: false,
    };
  }

  componentDidMount() {
    this.fetchUnpaidInvoices();
  }

  getContractNumber = () =>
    this.props.ServerSetting?.contractNumber ||
    this.props.logInUser?.contractNumber ||
    "";

  getCurrency = () => this.props.logInUser?.attributes?.currencyLabel || "AED";

  getRedirectUrl = () => `${window.location.origin}/payment/callback`;

  needsInvoices = (type = this.state.type) =>
    type === "INVOICE" || type === "PARTIAL";

  showAmountField = (type = this.state.type) => type !== "INVOICE";

  fetchUnpaidInvoices = (invoiceNumber = "") => {
    const cn = this.getContractNumber();
    if (!cn || !this.needsInvoices()) {
      this.setState({ invoices: [], selectedIds: [] });
      return;
    }

    this.setState({ invoicesLoader: true });
    instance({
      method: "GET",
      url: `/api/invoices/unpaid/list?cn=${encodeURIComponent(
        cn,
      )}&invoiceNumber=${encodeURIComponent(invoiceNumber || "")}`,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        const rows = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];
        this.setState({
          invoices: rows,
          invoicesLoader: false,
          selectedIds: [],
        });
      })
      .catch((error) => {
        this.setState({ invoices: [], invoicesLoader: false, selectedIds: [] });
        errorHandler(error, this.props.dispatch);
      });
  };

  handleTypeChange = (value) => {
    this.setState(
      {
        type: value,
        amount: value === "INVOICE" ? "" : this.state.amount,
        selectedIds: [],
      },
      () => {
        if (this.needsInvoices(value)) {
          this.fetchUnpaidInvoices(this.state.invoiceSearch);
        } else {
          this.setState({ invoices: [] });
        }
      },
    );
  };

  handleSearchInvoices = () => {
    this.fetchUnpaidInvoices(this.state.invoiceSearch);
  };

  toggleSelectAll = (checked) => {
    this.setState({
      selectedIds: checked
        ? this.state.invoices.map((inv) => String(inv.id)).filter(Boolean)
        : [],
    });
  };

  toggleSelectOne = (id, checked) => {
    const key = String(id);
    this.setState((prev) => {
      const selectedIds = checked
        ? [...new Set([...prev.selectedIds, key])]
        : prev.selectedIds.filter((x) => x !== key);
      return { selectedIds };
    });
  };

  getInvoiceLabel = (inv) =>
    inv.invoiceNumber || inv.invoiceNo || inv.number || inv.id;

  getInvoiceBalanceNumber = (inv) => {
    const raw =
      inv?.invoicebalance ??
      inv?.invoiceBalance ??
      inv?.remainingBalance ??
      inv?.remainingAmount ??
      0;
    const n = parseFloat(raw);
    return Number.isNaN(n) ? 0 : n;
  };

  getRemainingBalance = (inv) => this.getInvoiceBalanceNumber(inv).toFixed(2);

  getTotalAmount = (inv) => {
    const raw = inv?.salenetamount ?? inv?.saleNetAmount ?? inv?.totalAmount ?? 0;
    const n = parseFloat(raw);
    return (Number.isNaN(n) ? 0 : n).toFixed(2);
  };

  getSelectedInvoicesAmount = () => {
    const { selectedIds, invoices } = this.state;
    return (invoices || [])
      .filter((inv) => selectedIds.includes(String(inv.id)))
      .reduce((sum, inv) => sum + this.getInvoiceBalanceNumber(inv), 0);
  };

  isFormValid = () => {
    const { type, amount, description, selectedIds } = this.state;
    if (!type || !description?.trim()) return false;
    if (this.needsInvoices() && !selectedIds.length) return false;
    if (type === "INVOICE") {
      return this.getSelectedInvoicesAmount() > 0;
    }
    if (this.showAmountField()) {
      const n = parseFloat(amount);
      if (amount === "" || Number.isNaN(n) || n <= 0) return false;
    }
    return true;
  };

  buildPayload = () => {
    const { type, amount, description, selectedIds, invoices } = this.state;
    const selectedInvoices = invoices.filter((inv) =>
      selectedIds.includes(String(inv.id)),
    );
    const invoiceId = selectedIds.join(",");
    const invoiceLabels = selectedInvoices
      .map((inv) => this.getInvoiceLabel(inv))
      .join(", ");

    const payload = {
      contractNumber: this.getContractNumber(),
      currency: this.getCurrency(),
      redirectUrl: this.getRedirectUrl(),
      description: description.trim(),
      idempotentKey: generateIdempotentKey(),
      type,
    };

    if (this.needsInvoices()) {
      payload.invoiceId = invoiceId;
      if (!payload.description) {
        payload.description = `Payment for invoices ${invoiceLabels || invoiceId}`;
      }
    }

    if (type === "INVOICE") {
      payload.amount = this.getSelectedInvoicesAmount();
    } else if (this.showAmountField()) {
      payload.amount = parseFloat(amount);
    }

    return payload;
  };

  extractTapRedirectUrl = (res) => {
    const raw =
      res?.transactionUrl ||
      res?.transaction?.url ||
      res?.url ||
      res?.paymentUrl ||
      res?.redirect?.url ||
      null;
    if (!raw || typeof raw !== "string") return null;
    // API may HTML-encode query separators as &amp;
    return raw.replace(/&amp;/g, "&");
  };

  extractChargeId = (res) =>
    res?.chargeId || res?.charge_id || res?.id || res?.transaction?.id || null;

  submitForm = () => {
    if (!this.isFormValid() || this.state.submitLoader) return;

    const payload = this.buildPayload();
    const tapWindow = window.open("about:blank", "_blank");
    this.setState({ submitLoader: true });

    instance({
      method: "POST",
      url: "/api/payments/tappayment",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      data: payload,
    })
      .then((res) => {
        const chargeId = this.extractChargeId(res);
        if (chargeId) {
          sessionStorage.setItem("tapChargeId", String(chargeId));
        }
        const tapUrl = this.extractTapRedirectUrl(res);
        if (tapUrl) {
          if (tapWindow && !tapWindow.closed) {
            tapWindow.location.href = tapUrl;
          } else {
            const opened = window.open(tapUrl, "_blank");
            if (!opened) window.location.assign(tapUrl);
          }
          this.setState({ submitLoader: false });
          if (this.props.onSuccess) {
            this.props.onSuccess(res);
          }
          return;
        }
        if (tapWindow && !tapWindow.closed) tapWindow.close();
        this.setState({ submitLoader: false });
        if (this.props.onSuccess) {
          this.props.onSuccess(res);
        }
      })
      .catch((error) => {
        if (tapWindow && !tapWindow.closed) tapWindow.close();
        this.setState({ submitLoader: false });
        errorHandler(error, this.props.dispatch);
      });
  };

  renderInvoiceTable() {
    const { translate } = this.props;
    const { invoices, selectedIds, invoicesLoader, invoiceSearch } = this.state;
    const allSelected =
      invoices.length > 0 && selectedIds.length === invoices.length;

    return (
      <div
        style={{
          marginTop: 16,
          border: "1px dashed #c5c5c5",
          borderRadius: 6,
          padding: 12,
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={8} md={9}>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={translate("searchInvoice") || "Search"}
              value={invoiceSearch}
              onChange={(e) => this.setState({ invoiceSearch: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") this.handleSearchInvoices();
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button size="small" onClick={this.handleSearchInvoices}>
              {translate("search") || translate("search") || "Search"}
            </Button>
          </Grid>
        </Grid>

        {invoicesLoader ? (
          <Loader />
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            style={{ marginTop: 8 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < invoices.length
                      }
                      onChange={(e) => this.toggleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    {translate("invoiceNumber") || "InvoiceNo"}
                  </TableCell>
                  <TableCell>
                    {translate("contractNumber") || "Contract Number"}
                  </TableCell>
                  <TableCell>
                    {translate("remainingBalance") || "Remaining Balance"}
                  </TableCell>
                  <TableCell>
                    {translate("totalAmount") || "Total Amount"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {translate("noRecordFound") || "No records found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((inv) => {
                    const id = String(inv.id);
                    const checked = selectedIds.includes(id);
                    return (
                      <TableRow key={id} hover selected={checked}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={checked}
                            onChange={(e) =>
                              this.toggleSelectOne(id, e.target.checked)
                            }
                          />
                        </TableCell>
                        <TableCell>{this.getInvoiceLabel(inv)}</TableCell>
                        <TableCell>
                          {inv.contractNumber || this.getContractNumber()}
                        </TableCell>
                        <TableCell>{this.getRemainingBalance(inv)}</TableCell>
                        <TableCell>{this.getTotalAmount(inv)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {this.needsInvoices() && !selectedIds.length ? (
          <div style={{ marginTop: 8, color: "#c62828", fontSize: 13 }}>
            {translate("selectAtLeastOneInvoice") ||
              "Please select at least one invoice"}
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    const { translate, onCancel } = this.props;
    const { type, amount, description, submitLoader } = this.state;

    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>
            {translate("addPayment") || "Add Payment"}
          </h3>
          <Button size="small" className="button-white" onClick={onCancel}>
            {translate("back") || "BACK"}
          </Button>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              required
              fullWidth
              margin="dense"
              variant="outlined"
              label={translate("type") || "Type"}
              value={type}
              onChange={(e) => this.handleTypeChange(e.target.value)}
            >
              {PAYMENT_TYPES.map((option) => (
                <MenuItem key={option} value={option}>
                  {translate(option.toLowerCase()) || option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {this.showAmountField() && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                required
                fullWidth
                margin="dense"
                variant="outlined"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                label={translate("amount") || "Amount"}
                value={amount}
                onChange={(e) => this.setState({ amount: e.target.value })}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={translate("currency") || "Currency"}
              value={this.getCurrency()}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={translate("contractNumber") || "Contract Number"}
              value={this.getContractNumber()}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextField
              required
              fullWidth
              margin="dense"
              variant="outlined"
              label={translate("description") || "Description"}
              value={description}
              onChange={(e) => this.setState({ description: e.target.value })}
            />
          </Grid>
        </Grid>

        {this.needsInvoices() ? this.renderInvoiceTable() : null}

        <div
          style={{
            display: "flex",
            gap: 12,
            marginTop: 16,
            alignItems: "center",
          }}
        >
          <Button
            size="small"
            className="button-white"
            onClick={onCancel}
            disabled={submitLoader}
          >
            {translate("sharedCancel") || "Cancel"}
          </Button>
          <Button
            size="small"
            onClick={this.submitForm}
            disabled={!this.isFormValid() || submitLoader}
          >
            {submitLoader
              ? translate("loading") || "Loading..."
              : translate("payNow") || translate("sharedCreate") || "Pay Now"}
          </Button>
        </div>
      </div>
    );
  }
}

export default AddPaymentForm;
